import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, Search, Lock, Loader2, CheckCircle2, FolderOpen, ImageIcon, AlertCircle } from 'lucide-react';
import { toast } from '../utils/toast';
const API = (window.__API_BASE || '') + '/api';

/**
 * ImagePickerModal — reusable fullscreen picker that surfaces every keyframe
 * the user has access to (their own projects + projects they collaborate on)
 * so any image-input recipe can reuse an existing generation instead of
 * forcing a fresh upload.
 *
 * Wiring:
 *   <ImagePickerModal
 *      open={boolean}
 *      onClose={() => ...}
 *      onSelect={(file: File, dataUrl: string, metadata: {...}) => ...}
 *      title="Pick a keyframe"           // optional
 *      lockedOnlyDefault={false}         // optional
 *   />
 *
 * onSelect contract: receives a native File (PNG), a base64 data URL for
 * preview, and the original shot metadata so the caller can decide what
 * to do with it (typically: setImageFile(file); setImagePreview(dataUrl)).
 */
export default function ImagePickerModal({
  open,
  onClose,
  onSelect,
  title = 'Pick from your projects',
  lockedOnlyDefault = false,
}) {
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [selectedProject, setSelectedProject] = useState(null); // null = All projects
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [lockedOnly, setLockedOnly] = useState(!!lockedOnlyDefault);
  const [selectedShotId, setSelectedShotId] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const debounceRef = useRef(null);

  const PAGE_SIZE = 60;

  // Debounce search input → searchDebounced
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchDebounced(searchInput.trim()), 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchInput, open]);

  const fetchPage = useCallback(async (opts = {}) => {
    const { append = false } = opts;
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(append ? items.length : 0));
    if (selectedProject) params.set('project_id', selectedProject);
    if (searchDebounced) params.set('search', searchDebounced);
    if (lockedOnly) params.set('locked_only', 'true');

    try {
      if (append) setLoadingMore(true); else setLoading(true);
      setError('');
      const res = await axios.get(`${API}/my-keyframes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTotal(res.data.total || 0);
      setProjects(res.data.projects || []);
      setItems(prev => append ? [...prev, ...(res.data.items || [])] : (res.data.items || []));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load keyframes';
      setError(msg);
      if (!append) setItems([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [items.length, selectedProject, searchDebounced, lockedOnly]);

  // Initial + filter-change fetch
  useEffect(() => {
    if (!open) return;
    // Reset page when filters change (not on load-more)
    setSelectedShotId(null);
    fetchPage({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedProject, searchDebounced, lockedOnly]);

  // Reset state on open/close
  useEffect(() => {
    if (!open) {
      setSearchInput('');
      setSearchDebounced('');
      setSelectedProject(null);
      setLockedOnly(!!lockedOnlyDefault);
      setSelectedShotId(null);
      setItems([]);
      setError('');
    }
  }, [open, lockedOnlyDefault]);

  const selectedItem = useMemo(
    () => items.find(i => i.shot_id === selectedShotId) || null,
    [items, selectedShotId]
  );

  const handleConfirm = async () => {
    if (!selectedItem || confirming) return;
    setConfirming(true);
    try {
      // Fetch the image via our same-origin proxy endpoint. Direct S3 fetches
      // fail inside sandboxed/mobile webviews with
      // "Request object could not be cloned" because the bucket advertises no
      // CORS headers. The proxy route streams the bytes back with an origin
      // the browser trusts, so the blob → File conversion works everywhere.
      const token = localStorage.getItem('token');
      const proxyUrl = `${API}/my-keyframes/fetch/${encodeURIComponent(selectedItem.shot_id)}`;
      const resp = await fetch(proxyUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      if (!blob.size) throw new Error('Empty image');
      const mime = blob.type || 'image/png';
      const ext = mime.includes('jpeg') ? 'jpg' : (mime.split('/')[1] || 'png');
      const safeName = `kf_${selectedItem.shot_id.slice(0, 8)}.${ext}`;
      const file = new File([blob], safeName, { type: mime });
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('FileReader failed'));
        reader.readAsDataURL(blob);
      });
      onSelect?.(file, dataUrl, selectedItem);
      onClose?.();
    } catch (err) {
      console.error('[ImagePickerModal] select failed', err);
      toast.error(err?.message || 'Failed to fetch keyframe image');
    } finally {
      setConfirming(false);
    }
  };

  if (!open) return null;

  const canLoadMore = items.length < total && !loading && !loadingMore;

  const content = (
    <div
      className="fixed inset-0 z-[9999] flex flex-col"
      style={{ background: '#050A10' }}
      data-testid="image-picker-modal"
    >
      {/* ═══ Header ═══ */}
      <div
        className="flex items-center justify-between px-5 py-3 flex-shrink-0"
        style={{ background: 'rgba(5,10,16,0.9)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0,240,255,0.08)', border: '1px solid rgba(0,240,255,0.2)' }}>
            <FolderOpen className="w-3.5 h-3.5" style={{ color: '#00F0FF' }} />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-white tracking-tight truncate">{title}</h2>
            <p className="text-[10px] text-slate-500 font-mono tracking-wider uppercase">
              {total > 0 ? `${total} keyframe${total === 1 ? '' : 's'} available` : (loading ? 'Loading…' : 'No keyframes yet')}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-white/5 transition-colors flex-shrink-0"
          data-testid="image-picker-close"
        >
          <X className="w-5 h-5 text-slate-500 hover:text-white transition-colors" />
        </button>
      </div>

      {/* ═══ Body ═══ */}
      <div className="flex-1 flex overflow-hidden">
        {/* ── Left rail: project facet ── */}
        <div
          className="hidden md:flex flex-col w-60 border-r flex-shrink-0 overflow-y-auto"
          style={{ background: '#0A1520', borderColor: 'rgba(255,255,255,0.06)' }}
          data-testid="image-picker-project-rail"
        >
          <div className="px-4 py-3 text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">
            Projects
          </div>
          <button
            onClick={() => setSelectedProject(null)}
            className={`mx-2 mb-1 flex items-center justify-between px-3 py-2 rounded-lg text-left transition-all ${
              selectedProject === null ? 'bg-cyan-500/10 text-cyan-300' : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
            style={selectedProject === null ? { border: '1px solid rgba(0,240,255,0.2)' } : { border: '1px solid transparent' }}
            data-testid="image-picker-project-all"
          >
            <span className="text-xs font-medium truncate">All projects</span>
            <span className="text-[10px] font-mono text-slate-500 ml-2">{total}</span>
          </button>
          {projects.map(p => (
            <button
              key={p.id}
              onClick={() => setSelectedProject(p.id)}
              className={`mx-2 mb-1 flex items-center justify-between px-3 py-2 rounded-lg text-left transition-all ${
                selectedProject === p.id ? 'bg-cyan-500/10 text-cyan-300' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
              style={selectedProject === p.id ? { border: '1px solid rgba(0,240,255,0.2)' } : { border: '1px solid transparent' }}
              data-testid={`image-picker-project-${p.id}`}
            >
              <span className="text-xs font-medium truncate pr-2">{p.name}</span>
              <span className="text-[10px] font-mono text-slate-500 flex-shrink-0">{p.count}</span>
            </button>
          ))}
          {projects.length === 0 && !loading && (
            <div className="px-4 py-2 text-[10px] text-slate-600">
              No projects with keyframes yet.
            </div>
          )}
        </div>

        {/* ── Main: filters + grid ── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Filter bar */}
          <div
            className="flex items-center gap-3 px-5 py-3 flex-shrink-0"
            style={{ background: 'rgba(10,21,32,0.5)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
          >
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search by shot description…"
                className="w-full pl-9 pr-3 py-2 rounded-lg text-xs text-white placeholder:text-slate-600 outline-none focus:ring-1"
                style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.06)' }}
                data-testid="image-picker-search"
              />
            </div>
            <button
              onClick={() => setLockedOnly(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium transition-all ${
                lockedOnly ? 'bg-cyan-500/10 text-cyan-300' : 'text-slate-400 hover:text-white'
              }`}
              style={{ border: `1px solid ${lockedOnly ? 'rgba(0,240,255,0.3)' : 'rgba(255,255,255,0.08)'}` }}
              data-testid="image-picker-locked-toggle"
            >
              <Lock className="w-3 h-3" />
              Locked only
            </button>
            {/* Mobile project selector */}
            <select
              value={selectedProject || ''}
              onChange={e => setSelectedProject(e.target.value || null)}
              className="md:hidden px-2 py-2 rounded-lg text-[11px] text-white outline-none"
              style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.06)' }}
              data-testid="image-picker-project-mobile"
            >
              <option value="">All projects</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.count})</option>
              ))}
            </select>
          </div>

          {/* Grid */}
          <div className="flex-1 overflow-y-auto p-5" data-testid="image-picker-grid">
            {loading && items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-xs font-mono tracking-widest uppercase">Loading</span>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-center px-6">
                <AlertCircle className="w-8 h-8 text-red-400/60" />
                <p className="text-xs text-red-300">{error}</p>
                <button
                  onClick={() => fetchPage({ append: false })}
                  className="mt-2 px-3 py-1.5 rounded-md text-[11px] text-cyan-300 hover:text-cyan-200"
                  style={{ border: '1px solid rgba(0,240,255,0.2)' }}
                >
                  Retry
                </button>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-500 text-center px-6">
                <ImageIcon className="w-10 h-10 text-slate-700" />
                <p className="text-sm">No keyframes match your filters.</p>
                <p className="text-[11px] text-slate-600">
                  Try clearing the search or switching projects. Keyframes appear here after a project finishes its Keyframe stage.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {items.map(it => {
                    const isSel = selectedShotId === it.shot_id;
                    const thumbUrl = it.keyframe_s3_url || it.keyframe_url;
                    return (
                      <button
                        key={it.shot_id}
                        onClick={() => setSelectedShotId(it.shot_id)}
                        onDoubleClick={() => { setSelectedShotId(it.shot_id); setTimeout(handleConfirm, 0); }}
                        className={`relative text-left rounded-xl overflow-hidden transition-all ${isSel ? 'ring-2' : 'hover:ring-1'}`}
                        style={{
                          background: '#0A1520',
                          border: '1px solid rgba(255,255,255,0.06)',
                          ...(isSel ? { boxShadow: '0 0 0 2px #00F0FF, 0 0 20px rgba(0,240,255,0.25)' } : {}),
                        }}
                        data-testid={`image-picker-item-${it.shot_id}`}
                      >
                        <div className="aspect-video w-full overflow-hidden bg-black">
                          <img
                            src={thumbUrl}
                            alt={it.description}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                          {it.keyframe_locked && (
                            <div className="absolute top-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider" style={{ background: 'rgba(0,0,0,0.7)', color: '#00F0FF' }}>
                              <Lock className="w-2.5 h-2.5" /> Locked
                            </div>
                          )}
                          {isSel && (
                            <div className="absolute top-2 right-2 rounded-full" style={{ background: '#00F0FF' }}>
                              <CheckCircle2 className="w-5 h-5 text-slate-900" />
                            </div>
                          )}
                        </div>
                        <div className="px-2.5 py-2">
                          <div className="flex items-center gap-1.5 text-[9px] font-mono text-slate-500 mb-0.5">
                            <span className="truncate">{it.project_name}</span>
                          </div>
                          <div className="flex items-center gap-1 text-[10px] text-slate-400">
                            <span className="font-mono text-cyan-300/80">{it.scene_number}·{it.shot_number}</span>
                          </div>
                          <p className="text-[11px] text-slate-300 line-clamp-2 mt-1">{it.description || 'No description'}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {canLoadMore && (
                  <div className="flex justify-center pt-5">
                    <button
                      onClick={() => fetchPage({ append: true })}
                      className="px-4 py-2 rounded-lg text-xs font-medium text-cyan-300 hover:text-cyan-200 transition-all"
                      style={{ background: 'rgba(0,240,255,0.06)', border: '1px solid rgba(0,240,255,0.2)' }}
                      data-testid="image-picker-load-more"
                    >
                      Load more ({total - items.length} remaining)
                    </button>
                  </div>
                )}
                {loadingMore && (
                  <div className="flex justify-center pt-4 text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ═══ Footer (confirm bar) ═══ */}
      <div
        className="flex items-center justify-between px-5 py-3 flex-shrink-0"
        style={{ background: 'rgba(5,10,16,0.9)', borderTop: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="text-[11px] text-slate-500 truncate pr-4">
          {selectedItem ? (
            <>
              <span className="text-cyan-300/80 font-mono">{selectedItem.scene_number}·{selectedItem.shot_number}</span>
              <span className="mx-1.5 text-slate-700">·</span>
              <span className="text-slate-400">{selectedItem.project_name}</span>
            </>
          ) : (
            'Tap a keyframe to select'
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-xs font-medium text-slate-400 hover:text-white transition-colors"
            data-testid="image-picker-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedItem || confirming}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #0EA5E9, #0284C7)' }}
            data-testid="image-picker-confirm"
          >
            {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Use this keyframe
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}
